import React, { useState, useContext, useCallback, useRef, useEffect } from 'react';
import 'twin.macro';
import { postComment } from 'services/comments';
import { Button } from '../button';
import { TextArea } from './FormParticals';
import { Icon } from '../icon';
import { LoaderIcon } from 'icons';
import { CommentsContext, UserContext } from '../context';
import { CommentListHeader } from '../comments';
import { commentsSortings } from '../../data/sortings';
import { gaEvent } from 'services/ga';
import { createTempComment } from './CommentForm';
import { formatDate, formatTime } from '../../lib/helpers';
import { notifyError } from '../notifications';

export const CommentFormFields = ({ formRef, message, setMessage, submit, commentPending }) => {
  return (
    <form ref={formRef} tw='mt-2'>
      <TextArea
        placeholder='Reaguursel'
        onChange={(e) => setMessage(e.target.value)}
        value={message}
      />
      <div tw='flex gap-4 justify-end items-center'>
        <Button
          label={
            !commentPending ? (
              'Verstuur'
            ) : (
              <Icon Type={LoaderIcon} tw='animate-spin h-5' color='input' size='small' />
            )
          }
          primary
          tw='float-right'
          onClick={submit}
          disabled={(message && message.length < 5) || !message || commentPending}
        />
      </div>
    </form>
  );
};

export const CommentReplyForm = ({
  comment,
  subComment,
  parent = { id: '', username: '' },
  setSubCommentForm,
  hide,
}) => {
  const {
    authorsList,
    itemId,
    commentsList,
    setCommentsList,
    commentsSort,
    setLastAddedComment,
    openComments,
  } = useContext(CommentsContext);
  const { user } = useContext(UserContext);
  const [message, setMessage] = useState('');
  const [commentPending, setCommentPending] = useState(false);
  const formRef = useRef();

  const { author, creation_datetime: creationDatetime } = comment;
  const isReply = authorsList.find((user) => user.id === author);
  const replyTo = isReply ? isReply.username : null;

  useEffect(() => {
    if (subComment) {
      setMessage(
        `@${replyTo} | ${formatDate(creationDatetime, true)} | ${formatTime(creationDatetime)}: `,
      );
    } else {
      setMessage('');
    }
  }, []);

  const submit = useCallback(async () => {
    setCommentPending(true);
    try {
      const {
        data: { id },
      } = await postComment({ itemId, parentId: parent.id.toString(), content: message });
      let newCommentsList = [];
      if (parent.id === '') {
        gaEvent('dmp_comments', 'placed_comment');
        newCommentsList = commentsList
          ? [...commentsList, createTempComment({ username: user.name, message, id })]
          : [createTempComment({ username: user.name, message, id })];
      } else {
        const oldParent = commentsList.find((item) => item.id === parent.id);
        const newParent = {
          ...oldParent,
          child_comments: oldParent.child_comments
            ? [
                ...oldParent.child_comments.map((child) => child),
                createTempComment({ username: user.name, message, id }),
              ]
            : [createTempComment({ username: user.name, message, id })],
        };
        newCommentsList = [
          ...commentsList.map((item) => (item.id !== parent.id ? item : newParent)),
        ];
        gaEvent('dmp_comments', 'placed_subcomment');
      }
      setMessage('');
      setSubCommentForm();
      setLastAddedComment(`${id}`);
      setCommentsList(commentsSortings(newCommentsList, commentsSort));
    } catch (error) {
      gaEvent('dmp_comments', 'placed_comment_reply_failed');
      notifyError(error);
    } finally {
      setCommentPending(false);
    }
  }, [message]);

  useEffect(() => {
    if (parent.id !== '' && formRef.current) {
      const textArea = formRef.current[0];
      const valueLength = textArea.value.length;
      textArea.setSelectionRange(valueLength, valueLength);
      textArea.focus();
    }
  }, [formRef && formRef.current]);

  return (
    <>
      <CommentListHeader subComment={parent.id !== ''} openComments={openComments} hideForm={hide}>
        <>
          {openComments && user && !hide && (
            <CommentFormFields {...{ formRef, message, setMessage, submit, commentPending }} />
          )}
        </>
      </CommentListHeader>
    </>
  );
};
